import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Banknote, ChevronRight, ShieldCheck } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { cn, formatDateTime, money, paymentMethodLabel } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  ModalHeader,
  PageHeader,
  Pagination,
  Skeleton,
  Table,
  Td,
  Th,
  usePagedList,
} from '@/components/ui';
import { MyDeclarations } from '@/components/MyDeclarations';
import { RepayModal } from '@/components/RepayModal';
import { StatusBadge } from '@/components/StatusBadge';
import { GUARANTOR_STATUS, INSTALLMENT_STATUS, LOAN_STATUS } from '@/lib/status';

export interface LoanDetailResponse {
  id: string;
  loanNumber: string;
  status: string;
  principal: string;
  totalRepayable: string;
  totalInterest: string;
  totalFees: string;
  totalPenalties: string;
  amountRepaid: string;
  outstanding: string;
  overdueAmount: string;
  daysInArrears: number;
  progress: number;
  installmentCount: number;
  repaymentFrequency: string;
  firstDueDate: string;
  finalDueDate: string;
  disbursedAt: string | null;
  member: { name: string; membershipNumber: string; email: string; phone: string };
  product: { name: string };
  nextPayment: { installmentNumber: number; dueDate: string; amount: string; isOverdue: boolean } | null;
  schedule: Array<{
    number: number;
    dueDate: string;
    dueDateFormatted: string;
    expected: string;
    paid: string;
    balance: string;
    status: string;
  }>;
  guarantors: Array<{
    id: string;
    name: string;
    membershipNumber: string;
    status: string;
    guaranteedAmount: string;
  }>;
  payments: Array<{
    reference: string;
    amount: string;
    date: string;
    method: string;
    transactionReference: string | null;
    recordedAt: string;
    recordedBy: string;
  }>;
  disbursement: { reference: string; amount: string; date: string; method: string } | null;
}

/** "5 Oct 2026" — short enough for a table cell, still unambiguous. */
function shortDate(value: string) {
  return new Date(value).toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Used by both the member portal and the admin loan page. */
export function LoanDetailView({ loan, admin }: { loan: LoanDetailResponse; admin?: boolean }) {
  const inArrears = loan.daysInArrears > 0;
  const [repaying, setRepaying] = useState(false);
  const [viewing, setViewing] = useState<LoanDetailResponse['payments'][number] | null>(null);
  const paidCount = loan.schedule.filter((row) => row.status === 'PAID').length;
  const percent = Math.round(loan.progress * 100);
  const schedule = usePagedList(loan.schedule, 12);
  const payments = usePagedList(loan.payments, 5);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow={loan.product.name}
        title={loan.loanNumber}
        description={admin ? `${loan.member.name} · ${loan.member.membershipNumber}` : undefined}
        status={<StatusBadge map={LOAN_STATUS} status={loan.status} className="text-sm" />}
      />

      {inArrears && (
        <Alert tone="danger" title={`${loan.daysInArrears} days in arrears`} className="mb-6">
          {money(loan.overdueAmount)} is past due. No late fee is charged — nothing is ever added to
          what you owe — but continued arrears suspend eligibility to borrow again.
        </Alert>
      )}

      {/*
        One card carries the whole position: what is left, how far along, and
        what to do next. The previous version spread these across four separate
        boxes that became a column of mostly-empty cards on a phone.
      */}
      <Card className="mb-6 overflow-hidden">
        <CardContent className="px-5 py-5 sm:px-6">
          {/*
            One figure leads: what is still owed. The original loan amount is
            not a second headline competing with it — it belongs in the line
            that explains the progress bar, which is where it actually means
            something.
          */}
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-secondary">
            Outstanding
          </p>
          <p
            className={cn(
              'numeric mt-1 text-[28px] font-semibold leading-none tracking-tight',
              inArrears ? 'text-danger' : 'text-foreground',
            )}
          >
            {money(loan.outstanding)}
          </p>

          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-success transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>

          <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-xs text-foreground-secondary">
            <p>
              <span className="numeric text-foreground">{money(loan.amountRepaid)}</span> of{' '}
              <span className="numeric">{money(loan.principal)}</span> repaid
            </p>
            <p className="numeric">
              {paidCount} of {loan.installmentCount} paid
            </p>
          </div>

          {/* Next payment and the action on it sit together. */}
          {loan.nextPayment ? (
            <div className="mt-5 rounded-xl border border-border p-4 sm:flex sm:items-center sm:justify-between sm:gap-6">
              <div className="flex items-baseline justify-between gap-3 sm:items-end sm:justify-start sm:gap-5">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-secondary">
                    Next payment
                  </p>
                  <p className="numeric mt-1 text-lg font-semibold leading-none text-foreground">
                    {money(loan.nextPayment.amount)}
                  </p>
                </div>
                <p
                  className={cn(
                    'shrink-0 text-right text-xs sm:text-left',
                    loan.nextPayment.isOverdue
                      ? 'font-semibold text-danger'
                      : 'text-foreground-secondary',
                  )}
                >
                  {loan.nextPayment.isOverdue ? 'Was due ' : 'Due '}
                  {formatLongDate(new Date(loan.nextPayment.dueDate))}
                </p>
              </div>

              {!admin && (
                <Button className="mt-3.5 w-full sm:mt-0 sm:w-auto" onClick={() => setRepaying(true)}>
                  <Banknote size={16} /> Repay
                </Button>
              )}
            </div>
          ) : (
            <p className="mt-5 rounded-xl border border-border bg-muted/40 p-4 text-sm text-foreground-secondary">
              Nothing outstanding on this loan.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="min-w-0 self-start lg:col-span-2">
          <CardHeader>
            <CardTitle>Repayment schedule</CardTitle>
          </CardHeader>

          {/*
            A six-column table cannot be made to fit a phone. Below sm it
            becomes one row per installment as a stacked block; the table is
            kept for the widths where it genuinely reads better.
          */}
          <CardContent className="px-0 pb-0">
            <ul className="divide-y divide-border sm:hidden">
              {schedule.rows.map((row) => (
                <li key={row.number} className="px-5 py-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={cn(
                          'numeric flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                          row.status === 'PAID'
                            ? 'bg-success-soft text-success-soft-foreground'
                            : row.status === 'OVERDUE'
                              ? 'bg-danger-soft text-danger-soft-foreground'
                              : 'bg-muted text-foreground-secondary',
                        )}
                      >
                        {row.number}
                      </span>
                      <div className="min-w-0">
                        <p className="numeric text-sm font-semibold text-foreground">
                          {money(row.expected)}
                        </p>
                        <p className="text-xs text-foreground-secondary">
                          {shortDate(row.dueDate)}
                        </p>
                      </div>
                    </div>
                    <StatusBadge map={INSTALLMENT_STATUS} status={row.status} />
                  </div>

                  {Number(row.paid) > 0 && row.status !== 'PAID' && (
                    <p className="numeric mt-2 pl-11 text-xs text-foreground-secondary">
                      {money(row.paid)} paid · {money(row.balance)} left
                    </p>
                  )}
                </li>
              ))}
            </ul>

            <div className="hidden sm:block">
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Due date</Th>
                    <Th numeric>Expected</Th>
                    <Th numeric>Paid</Th>
                    <Th numeric>Balance</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.rows.map((row) => (
                    <tr key={row.number} className="transition-colors hover:bg-muted/50">
                      <Td className="numeric">{row.number}</Td>
                      <Td className="numeric whitespace-nowrap text-foreground-secondary">
                        {shortDate(row.dueDate)}
                      </Td>
                      <Td numeric>{money(row.expected)}</Td>
                      <Td numeric className={Number(row.paid) > 0 ? 'text-success' : ''}>
                        {money(row.paid)}
                      </Td>
                      <Td
                        numeric
                        className={row.status === 'OVERDUE' ? 'font-semibold text-danger' : ''}
                      >
                        {money(row.balance)}
                      </Td>
                      <Td>
                        <StatusBadge map={INSTALLMENT_STATUS} status={row.status} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {schedule.meta.totalPages > 1 && (
              <Pagination meta={schedule.meta} onPageChange={schedule.setPage} />
            )}
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-6">
          {!admin && <MyDeclarations loanNumber={loan.loanNumber} />}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <ShieldCheck size={15} /> Guarantors
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {loan.guarantors.length === 0 && (
                <p className="text-sm text-foreground-secondary">No guarantors recorded.</p>
              )}
              {loan.guarantors.map((guarantor) => (
                <div
                  key={guarantor.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border px-3.5 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {guarantor.name}
                    </p>
                    {guarantor.membershipNumber && (
                      <p className="numeric truncate text-xs text-foreground-secondary">
                        {guarantor.membershipNumber}
                      </p>
                    )}
                  </div>
                  <StatusBadge map={GUARANTOR_STATUS} status={guarantor.status} showDot={false} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Payment history</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {loan.payments.length === 0 ? (
                <p className="text-sm leading-relaxed text-foreground-secondary">
                  Nothing recorded yet. Payments appear here once an officer has confirmed them.
                </p>
              ) : (
                payments.rows.map((payment) => (
                  <button
                    key={payment.reference}
                    type="button"
                    onClick={() => setViewing(payment)}
                    className="flex w-full items-center gap-3 rounded-xl border border-border px-3.5 py-2.5 text-left transition-colors hover:bg-accent"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="numeric text-sm font-semibold text-foreground">
                        {money(payment.amount)}
                      </p>
                      <p className="truncate text-xs text-foreground-secondary">
                        {formatLongDate(new Date(payment.date))}
                      </p>
                    </div>
                    <ChevronRight size={15} className="shrink-0 text-foreground-secondary" />
                  </button>
                ))
              )}
            </CardContent>
            {payments.meta.totalPages > 1 && (
              <Pagination meta={payments.meta} onPageChange={payments.setPage} className="px-5" />
            )}
          </Card>

          {loan.disbursement && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Disbursement</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 text-sm">
                <dl className="space-y-2">
                  <Line label="Reference" value={loan.disbursement.reference} />
                  <Line label="Amount" value={money(loan.disbursement.amount)} />
                  <Line label="Date" value={formatLongDate(new Date(loan.disbursement.date))} />
                  <Line label="Method" value={paymentMethodLabel(loan.disbursement.method)} />
                </dl>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {viewing && (
        <Modal open onClose={() => setViewing(null)}>
          <ModalHeader
            title={money(viewing.amount)}
            description="Confirmed and applied to this loan"
            onClose={() => setViewing(null)}
          />
          <dl className="space-y-2.5 p-6 text-sm">
            <Line label="Date received" value={formatLongDate(new Date(viewing.date))} />
            <Line label="Method" value={paymentMethodLabel(viewing.method)} />
            <Line label="Our reference" value={viewing.reference} />
            {viewing.transactionReference && (
              <Line label="Bank reference" value={viewing.transactionReference} />
            )}
            <Line label="Recorded" value={formatDateTime(viewing.recordedAt)} />
            <Line label="Recorded by" value={viewing.recordedBy} />
          </dl>
        </Modal>
      )}

      {repaying && !admin && (
        <RepayModal
          loanId={loan.id}
          loanNumber={loan.loanNumber}
          nextInstallment={
            loan.nextPayment
              ? {
                  installmentNumber: loan.nextPayment.installmentNumber,
                  dueDate: loan.nextPayment.dueDate,
                  amountDue: loan.nextPayment.amount,
                }
              : null
          }
          onClose={() => setRepaying(false)}
        />
      )}
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-foreground-secondary">{label}</dt>
      <dd className="numeric min-w-0 truncate text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}

export default function MemberLoanDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['my-loan', id],
    queryFn: () => api.get<LoanDetailResponse>(`/loans/my/${id}`),
    enabled: Boolean(id),
  });

  if (isLoading) return <Skeleton className="h-96" />;
  if (!data) return null;

  return <LoanDetailView loan={data} />;
}
